// components/ModerationQueue.tsx
// Content moderation queue for reviewing flagged posts

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { StatsCard } from './StatsCard';
import { fetchModerationQueue, getModerationStats, approvePost, rejectPost } from '../services/moderation';
import type { ModerationQueueItem } from '../types';
import './ModerationQueue.css';

export const ModerationQueue: React.FC = () => {
    const navigate = useNavigate();
    const { currentUser, signOut } = useAuth();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [queue, setQueue] = useState<ModerationQueueItem[]>([]);
    const [stats, setStats] = useState({
        queueSize: 0,
        pendingReports: 0,
        resolvedReports: 0,
        approvedPosts: 0,
        rejectedPosts: 0,
        approvalRate: 0
    });
    const [selectedItem, setSelectedItem] = useState<ModerationQueueItem | null>(null);
    const [actionLoading, setActionLoading] = useState(false);
    const [adminNote, setAdminNote] = useState('');

    useEffect(() => {
        loadQueue();
        loadStats();
    }, []);

    const loadQueue = async () => {
        try {
            setLoading(true);
            setError('');
            const data = await fetchModerationQueue(50);
            setQueue(data);
        } catch (err: any) {
            console.error('Error loading queue:', err);
            setError('Failed to load moderation queue');
        } finally {
            setLoading(false);
        }
    };

    const loadStats = async () => {
        try {
            const statsData = await getModerationStats();
            setStats(statsData);
        } catch (err: any) {
            console.error('Error loading stats:', err);
        }
    };

    const handleApprove = async (item: ModerationQueueItem, note?: string) => {
        if (!currentUser?.email || !currentUser?.uid) return;

        try {
            setActionLoading(true);
            await approvePost(item.post.id, currentUser.uid, currentUser.email, note || adminNote);
            await loadQueue();
            await loadStats();
            setSelectedItem(null);
            setAdminNote('');
            alert('Post approved successfully');
        } catch (err: any) {
            console.error('Error approving post:', err);
            alert('Failed to approve post');
        } finally {
            setActionLoading(false);
        }
    };

    const handleReject = async (item: ModerationQueueItem, permanentDelete: boolean = false, note?: string) => {
        if (!currentUser?.email || !currentUser?.uid) return;

        const confirmMsg = permanentDelete
            ? 'Are you sure you want to PERMANENTLY DELETE this post? This cannot be undone.'
            : 'Are you sure you want to reject (soft delete) this post?';

        if (!window.confirm(confirmMsg)) return;

        try {
            setActionLoading(true);
            await rejectPost(item.post.id, currentUser.uid, currentUser.email, note || adminNote, permanentDelete);
            await loadQueue();
            await loadStats();
            setSelectedItem(null);
            setAdminNote('');
            alert(permanentDelete ? 'Post deleted permanently' : 'Post rejected successfully');
        } catch (err: any) {
            console.error('Error rejecting post:', err);
            alert('Failed to reject post');
        } finally {
            setActionLoading(false);
        }
    };

    const handleSignOut = async () => {
        try {
            await signOut();
            navigate('/login');
        } catch (error) {
            console.error('Failed to sign out:', error);
        }
    };

    const formatDate = (date: Date): string => {
        const now = new Date();
        const diff = now.getTime() - date.getTime();
        const hours = Math.floor(diff / (1000 * 60 * 60));

        if (hours < 1) return 'Just now';
        if (hours < 24) return `${hours}h ago`;
        return date.toLocaleDateString();
    };

    return (
        <div className="moderation-container">
            <header className="moderation-header">
                <div className="header-content">
                    <div>
                        <h1>Moderation Queue</h1>
                        <p className="header-subtitle">Review flagged content and reports</p>
                    </div>
                    <div className="header-actions">
                        <button onClick={() => navigate('/dashboard')} className="back-button">
                            � Back to Dashboard
                        </button>
                        <span className="user-email">{currentUser?.email}</span>
                        <button onClick={handleSignOut} className="logout-button">
                            Sign Out
                        </button>
                    </div>
                </div>
            </header>

            <div className="moderation-main">
                <section className="stats-section">
                    <h2>=� Queue Stats</h2>
                    <div className="stats-grid">
                        <StatsCard
                            title="Pending"
                            value={stats.queueSize}
                            icon="�"
                            color="yellow"
                            subtitle="Items in queue"
                        />
                        <StatsCard
                            title="Reports"
                            value={stats.pendingReports}
                            icon="=�"
                            color="red"
                            subtitle="Pending reports"
                        />
                        <StatsCard
                            title="Resolved"
                            value={stats.resolvedReports}
                            icon=""
                            color="green"
                            subtitle="Total resolved"
                        />
                        <StatsCard
                            title="Approval"
                            value={`${stats.approvalRate}%`}
                            icon="=�"
                            color="blue"
                            subtitle="Approval rate"
                        />
                    </div>
                </section>

                <section className="queue-section">
                    <div className="queue-header">
                        <h2>=� Flagged Posts</h2>
                        <button onClick={() => { loadQueue(); loadStats(); }} className="refresh-button">
                            = Refresh
                        </button>
                    </div>

                    {loading && (
                        <div className="loading-state">
                            <div className="loading-spinner"></div>
                            <p>Loading queue...</p>
                        </div>
                    )}

                    {error && (
                        <div className="error-state">
                            <p>{error}</p>
                            <button onClick={loadQueue} className="retry-button">Retry</button>
                        </div>
                    )}

                    {!loading && !error && queue.length === 0 && (
                        <div className="empty-state">
                            <div className="empty-icon">(</div>
                            <h3>Queue is empty!</h3>
                            <p>No flagged posts to review at this time.</p>
                        </div>
                    )}

                    {!loading && !error && queue.length > 0 && (
                        <div className="queue-list">
                            {queue.map((item) => (
                                <div key={item.post.id} className="queue-item">
                                    <div className="item-header">
                                        <span className="item-warning">�</span>
                                        <div className="item-info">
                                            <h3 className="item-title">{item.post.title}</h3>
                                            <div className="item-meta">
                                                <span>By: {item.post.authorName || item.post.authorEmail}</span>
                                                <span>"</span>
                                                <span>Flagged: {formatDate(item.flaggedAt)}</span>
                                                <span>"</span>
                                                <span>Type: {item.post.type}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="item-reason">
                                        <strong>Reason:</strong> {item.flaggedReason} ({item.reportCount} report{item.reportCount !== 1 ? 's' : ''})
                                    </div>

                                    {item.reports.length > 0 && (
                                        <div className="item-reports">
                                            <strong>Reports:</strong>
                                            {item.reports.slice(0, 2).map((report) => (
                                                <div key={report.id} className="report-item">
                                                    <span className="report-category">{report.category}</span>
                                                    {report.details && <span className="report-details">- {report.details}</span>}
                                                </div>
                                            ))}
                                            {item.reports.length > 2 && (
                                                <div className="report-more">+{item.reports.length - 2} more reports</div>
                                            )}
                                        </div>
                                    )}

                                    <div className="item-actions">
                                        <button
                                            onClick={() => setSelectedItem(item)}
                                            className="action-button view-button"
                                        >
                                            View Details
                                        </button>
                                        <button
                                            onClick={() => handleApprove(item, 'Quick approval')}
                                            className="action-button approve-button"
                                            disabled={actionLoading}
                                        >
                                             Quick Approve
                                        </button>
                                        <button
                                            onClick={() => handleReject(item, false, 'Quick rejection')}
                                            className="action-button reject-button"
                                            disabled={actionLoading}
                                        >
                                            L Quick Reject
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </section>
            </div>

            {selectedItem && (
                <div className="modal-overlay" onClick={() => setSelectedItem(null)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Review Post</h2>
                            <button onClick={() => setSelectedItem(null)} className="close-button">�</button>
                        </div>

                        <div className="modal-body">
                            <div className="post-details">
                                <h3>{selectedItem.post.title}</h3>
                                <div className="post-meta">
                                    <div><strong>Author:</strong> {selectedItem.post.authorName || selectedItem.post.authorEmail}</div>
                                    <div><strong>Type:</strong> {selectedItem.post.type}</div>
                                    <div><strong>Category:</strong> {selectedItem.post.category}</div>
                                    <div><strong>Published:</strong> {selectedItem.post.publishedAt?.toLocaleDateString() || 'N/A'}</div>
                                    <div><strong>Views:</strong> {selectedItem.post.viewCount || 0}</div>
                                    <div><strong>Compliments:</strong> {selectedItem.post.complimentCount || 0}</div>
                                </div>

                                <div className="post-content-preview">
                                    <strong>Content Preview:</strong>
                                    <div className="content-box">
                                        {selectedItem.post.caption || selectedItem.post.articleContent?.substring(0, 500) || 'No content preview available'}
                                    </div>
                                </div>
                            </div>

                            <div className="reports-section">
                                <h4>Reports ({selectedItem.reports.length})</h4>
                                {selectedItem.reports.map((report) => (
                                    <div key={report.id} className="report-detail">
                                        <div><strong>Category:</strong> {report.category}</div>
                                        <div><strong>Details:</strong> {report.details || 'No details provided'}</div>
                                        <div><strong>Reported:</strong> {report.timestamp.toLocaleDateString()}</div>
                                    </div>
                                ))}
                            </div>

                            <div className="admin-action-section">
                                <h4>Admin Action</h4>
                                <textarea
                                    placeholder="Optional admin note..."
                                    value={adminNote}
                                    onChange={(e) => setAdminNote(e.target.value)}
                                    className="admin-note-input"
                                    rows={3}
                                />

                                <div className="modal-actions">
                                    <button
                                        onClick={() => handleApprove(selectedItem, adminNote)}
                                        className="modal-button approve"
                                        disabled={actionLoading}
                                    >
                                         Approve & Restore
                                    </button>
                                    <button
                                        onClick={() => handleReject(selectedItem, false, adminNote)}
                                        className="modal-button reject"
                                        disabled={actionLoading}
                                    >
                                        L Reject (Soft Delete)
                                    </button>
                                    <button
                                        onClick={() => handleReject(selectedItem, true, adminNote)}
                                        className="modal-button delete"
                                        disabled={actionLoading}
                                    >
                                        =� Permanently Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
